import type {
  DataPaginationResponse_PersonalAccountNotification_,
  IResponse,
  NotificationsSubscriptionRequestSerializer,
  NotificationStatusRequestSerializer,
  PaginationResponse_PersonalAccountNotification_,
  PersonalAccountMessageSubscriptionRequestSerializer,
  PersonalAccountMessageSubscriptionSerializer,
  PromocodeNotFoundAPIModel,
} from "@litres-libs/types";
import isEqual from "lodash/isEqual";
import { rtkqApi } from "../api/rtkqApi";
import { TagsType } from "../constants";

export const notificationsApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getNotifications: build.query<
      DataPaginationResponse_PersonalAccountNotification_,
      {
        limit?: number;
        before?: string;
        after?: string;
      } | void
    >({
      query: args => ({
        url: "/foundation/api/users/me/notifications/personal-account",
        method: "GET",
        params: args,
      }),
      transformResponse: (
        response: PaginationResponse_PersonalAccountNotification_
      ) => {
        return response.payload;
      },
      providesTags: result =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.NOTIFICATIONS as const,
                id,
              })),
              { type: TagsType.NOTIFICATIONS, id: "NOTIFICATION-ITEMS" },
            ]
          : [{ type: TagsType.NOTIFICATIONS, id: "NOTIFICATION-ITEMS" }],
      merge: (currentCache, newResponse, { arg }) => {
        if (arg?.after) {
          // при ручной инвалидации кэша арты могут дублироваться, поэтому добавлена фильтрация по id
          const newData = newResponse.data.filter(
            item => !currentCache.data.find(current => current.id === item.id)
          );
          currentCache.data.push(...newData);
        } else {
          currentCache.data = newResponse.data;
        }

        currentCache.pagination = newResponse.pagination;
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
    }),

    setStatus: build.mutation<
      PromocodeNotFoundAPIModel,
      NotificationStatusRequestSerializer
    >({
      query: body => ({
        url: "/foundation/api/users/me/notifications/status",
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { notifications }) => [
        ...notifications.map(notification => ({
          type: TagsType.NOTIFICATIONS,
          id: notification.id,
        })),
        TagsType.USER_DETAILED_DATA,
      ],
      onQueryStarted({ notifications }, { dispatch }) {
        dispatch(
          notificationsApi.util.updateQueryData(
            "getNotifications",
            {},
            cache => {
              const viewedItemsIds = notifications.map(({ id }) => id);
              const viewedItems = cache.data.filter(item =>
                viewedItemsIds.includes(item.id)
              );
              viewedItems.forEach(item => {
                item.is_viewed = true;
              });
              Object.assign(cache, {
                data: cache.data.slice(),
              });
            }
          )
        );
      },
    }),

    createNotification: build.mutation<
      IResponse,
      NotificationsSubscriptionRequestSerializer
    >({
      query: body => ({
        url: "/foundation/api/users/me/notifications/subscriptions/email",
        method: "PUT",
        body,
      }),
    }),

    getNotificationsSettings: build.query<
      PersonalAccountMessageSubscriptionSerializer,
      void
    >({
      query: () => ({
        url: "/foundation/api/users/me/notifications/settings",
        method: "GET",
      }),
      transformResponse: (response: any) => {
        return response.payload.data;
      },
      providesTags: [{ type: TagsType.NOTIFICATIONS, id: "SETTINGS" }],
    }),

    updateNotificationsSettings: build.mutation<
      IResponse,
      PersonalAccountMessageSubscriptionRequestSerializer
    >({
      query: body => ({
        url: "/foundation/api/users/me/notifications/personal-account/settings",
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: TagsType.NOTIFICATIONS, id: "SETTINGS" }],
    }),
  }),
});
